import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Download, FileText, Trash2, Plus, ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import {
  ARTICLE_CITATION_OPTIONS,
  citationRuleFor,
  useCitationStyle,
} from "@/lib/use-citation-style";
import { buildArticlePdf } from "@/lib/legal-article-pdf";

export const Route = createFileRoute("/education/writing/article-editor")({
  head: () => ({
    meta: [
      {
        title:
          "Legal Article Editor — Draft & Download as Formatted PDF | Nkadimeng Danny",
      },
      {
        name: "description",
        content:
          "Draft your legal article inside the browser and download it as a properly formatted PDF: Times New Roman 12pt, 1.5 spacing, justified, with headings, footnotes and a bibliography.",
      },
      { property: "og:title", content: "Legal Article Editor" },
      {
        property: "og:description",
        content:
          "Compose your legal article and export it as a submission-ready PDF with citations and headings.",
      },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://nkadimengdannylcs.com/education/writing/article-editor",
      },
    ],
  }),
  component: ArticleEditorPage,
});

type SectionInput = { heading: string; body: string };

const DEFAULT_SECTIONS: SectionInput[] = [
  { heading: "Introduction", body: "" },
  { heading: "Legal framework", body: "" },
  { heading: "Analysis", body: "" },
  { heading: "Counter-argument", body: "" },
  { heading: "Conclusion", body: "" },
];

const STORAGE_KEY = "ndlcs.article.editor.v1";

type Draft = {
  title: string;
  author: string;
  affiliation: string;
  abstract: string;
  sections: SectionInput[];
  footnotes: string[];
  bibliography: string[];
};

const EMPTY: Draft = {
  title: "",
  author: "",
  affiliation: "",
  abstract: "",
  sections: DEFAULT_SECTIONS,
  footnotes: [""],
  bibliography: [""],
};

function ArticleEditorPage() {
  const { style, setStyle, hydrated } = useCitationStyle();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setDraft({ ...EMPTY, ...JSON.parse(saved) });
    } catch {
      /* ignore */
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    } catch {
      /* ignore */
    }
  }, [draft, loaded]);

  const rule = useMemo(() => citationRuleFor(style), [style]);
  const styleLabel =
    ARTICLE_CITATION_OPTIONS.find((o) => o.slug === style)?.label ?? "OSCOLA";

  const wordCount = useMemo(() => {
    const all = [
      draft.abstract,
      ...draft.sections.map((s) => `${s.heading} ${s.body}`),
    ].join(" ");
    const words = all.trim().split(/\s+/).filter(Boolean);
    return words.length;
  }, [draft]);

  const withinLimit = wordCount >= 2000 && wordCount <= 3000;

  function updateSection(i: number, patch: Partial<SectionInput>) {
    setDraft((d) => ({
      ...d,
      sections: d.sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    }));
  }

  function addSection() {
    setDraft((d) => ({
      ...d,
      sections: [...d.sections, { heading: "New section", body: "" }],
    }));
  }

  function removeSection(i: number) {
    setDraft((d) => ({
      ...d,
      sections: d.sections.filter((_, idx) => idx !== i),
    }));
  }

  function handleDownload() {
    if (!draft.title.trim()) {
      toast.error("Add a title before downloading.");
      return;
    }
    try {
      const pdf = buildArticlePdf({
        title: draft.title,
        author: draft.author,
        affiliation: draft.affiliation,
        citationStyleLabel: styleLabel,
        abstract: draft.abstract,
        sections: draft.sections,
        footnotes: draft.footnotes,
        bibliography: draft.bibliography,
      });
      const filename =
        (draft.title || "legal-article")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "") + ".pdf";
      pdf.save(filename);
      toast.success("PDF downloaded — check your Downloads folder.");
    } catch (e) {
      console.error(e);
      toast.error("Could not build the PDF. Please try again.");
    }
  }

  return (
    <div className="container-narrow py-10 md:py-14">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/education/legal-article-guide"
            className="inline-flex items-center gap-1 text-sm text-navy hover:underline"
          >
            <ArrowLeft className="size-4" /> Back to the writing guide
          </Link>
          <h1 className="mt-2 font-display text-3xl text-navy md:text-4xl">
            Legal article editor
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Draft in the browser. Everything autosaves locally. Download a
            submission-ready PDF: Times New Roman 12pt, 1.5 spacing, justified,
            headings + footnotes + bibliography.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-medium text-white hover:bg-navy/90"
        >
          <Download className="size-4" /> Download PDF
        </button>
      </div>

      <div className="mb-6 grid gap-3 rounded-lg border border-border bg-card p-5 md:grid-cols-3">
        <div className="md:col-span-2">
          <label className="text-xs font-semibold uppercase tracking-widest text-navy">
            Citation style
          </label>
          <select
            className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm"
            value={hydrated ? style : "oscola"}
            onChange={(e) => setStyle(e.target.value as never)}
          >
            {ARTICLE_CITATION_OPTIONS.map((opt) => (
              <option key={opt.slug} value={opt.slug}>
                {opt.label} — {opt.note}
              </option>
            ))}
          </select>
          <p className="mt-2 text-xs text-muted-foreground">
            <strong className="text-navy">Rule:</strong> {rule.keyRule}
            <br />
            <strong className="text-navy">Placement:</strong> {rule.placement}
          </p>
        </div>
        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
          <p className="font-semibold text-navy">Word count</p>
          <p
            className={
              withinLimit
                ? "text-lg font-bold text-green-700"
                : "text-lg font-bold text-amber-700"
            }
          >
            {wordCount.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">
            Target: 2,000 – 3,000 words (Task 1)
          </p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} />
        <Field label="Author" value={draft.author} onChange={(v) => setDraft({ ...draft, author: v })} />
        <Field
          label="Affiliation / student number"
          value={draft.affiliation}
          onChange={(v) => setDraft({ ...draft, affiliation: v })}
        />
      </div>

      <div className="mt-4">
        <Textarea
          label="Abstract (optional)"
          value={draft.abstract}
          rows={3}
          onChange={(v) => setDraft({ ...draft, abstract: v })}
        />
      </div>

      <h2 className="mt-8 font-display text-xl text-navy">Sections</h2>
      <div className="mt-3 space-y-4">
        {draft.sections.map((s, i) => (
          <div
            key={i}
            className="rounded-lg border border-border bg-card p-4"
          >
            <div className="flex items-center gap-2">
              <span className="flex size-7 items-center justify-center rounded-full bg-navy text-xs font-semibold text-white">
                {i + 1}
              </span>
              <input
                type="text"
                aria-label={`Section ${i + 1} heading`}
                value={s.heading}
                onChange={(e) => updateSection(i, { heading: e.target.value })}
                className="flex-1 rounded-md border border-input bg-background p-2 text-sm font-semibold"
              />
              <button
                type="button"
                aria-label={`Remove section ${i + 1}`}
                onClick={() => removeSection(i)}
                className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
            <textarea
              aria-label={`Section ${i + 1} body`}
              value={s.body}
              onChange={(e) => updateSection(i, { body: e.target.value })}
              rows={8}
              className="mt-3 w-full rounded-md border border-input bg-background p-3 text-sm"
              placeholder="Write the section. Use blank lines between paragraphs. Reference footnotes as [1], [2]…"
            />
          </div>
        ))}
        <button
          type="button"
          onClick={addSection}
          className="inline-flex items-center gap-2 rounded-md border border-dashed border-input px-4 py-2 text-sm text-navy hover:bg-accent"
        >
          <Plus className="size-4" /> Add section
        </button>
      </div>

      <ListEditor
        title="Footnotes"
        description={`Numbered in order. ${rule.placement}`}
        items={draft.footnotes}
        onChange={(items) => setDraft({ ...draft, footnotes: items })}
        placeholder={`e.g. ${
          style === "oscola"
            ? "Carmichele v Minister of Safety and Security 2001 (4) SA 938 (CC) [44]."
            : style === "bluebook"
              ? "Brown v. Board of Education, 347 U.S. 483, 495 (1954)."
              : "Author, 'Title' (Year) journal page."
        }`}
      />

      <ListEditor
        title="Bibliography"
        description={rule.bibliographyLabel}
        items={draft.bibliography}
        onChange={(items) => setDraft({ ...draft, bibliography: items })}
        placeholder="One reference per row (alphabetical)."
      />

      <div className="mt-10 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={handleDownload}
          className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-medium text-white hover:bg-navy/90"
        >
          <FileText className="size-4" /> Download formatted PDF
        </button>
        <button
          type="button"
          onClick={() => {
            setDraft(EMPTY);
            toast.success("Draft cleared.");
          }}
          className="inline-flex items-center gap-2 rounded-md border border-input px-4 py-2 text-sm text-navy hover:bg-accent"
        >
          <Trash2 className="size-4" /> Clear draft
        </button>
        <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          <Save className="size-3.5" /> Autosaved locally in your browser.
        </span>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-widest text-navy">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm"
      />
    </label>
  );
}

function Textarea({
  label,
  value,
  onChange,
  rows = 4,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-widest text-navy">
        {label}
      </span>
      <textarea
        value={value}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-input bg-background p-3 text-sm"
      />
    </label>
  );
}

function ListEditor({
  title,
  description,
  items,
  onChange,
  placeholder,
}: {
  title: string;
  description: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
}) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-xl text-navy">{title}</h2>
      <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      <div className="mt-3 space-y-2">
        {items.map((v, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="mt-2 w-6 text-right text-xs font-semibold text-navy">
              {i + 1}.
            </span>
            <textarea
              value={v}
              rows={2}
              placeholder={placeholder}
              onChange={(e) =>
                onChange(items.map((it, idx) => (idx === i ? e.target.value : it)))
              }
              className="flex-1 rounded-md border border-input bg-background p-2 text-sm"
            />
            <button
              type="button"
              aria-label={`Remove ${title} entry ${i + 1}`}
              onClick={() => onChange(items.filter((_, idx) => idx !== i))}
              className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange([...items, ""])}
          className="inline-flex items-center gap-2 rounded-md border border-dashed border-input px-3 py-1.5 text-xs text-navy hover:bg-accent"
        >
          <Plus className="size-3.5" /> Add entry
        </button>
      </div>
    </section>
  );
}
